import { at } from 'lodash'
import { iconToString } from '../helpers/icon'
import Attrs from '../models/Attrs'
import type { BaseBuilderVersion, CustomButton, Flow, FlowPage } from '../models/types'
import { generateElementSelectors } from '../styles/selectors'
import replaceStorageURLsFromFirebaseToCloudflare from '../helpers/replaceStorageURLsFromFirebaseToCloudflare'
import renderTemplate from '../helpers/templating'
import { addLanguageController, getBaseBuilderVersion } from '../helpers/utils'

const CONTROLLER = 'components--custom-button'

export default function renderCustomButton(
  component: CustomButton,
  attrs: Attrs,
  page: FlowPage,
  flow: Flow,
  editingMode: boolean,
  userData?: { [key: string]: unknown }
): string {
  const builderVersion = getBaseBuilderVersion(flow.builder_version)
  const useWrapper = component.action === 'open-url'
  const useButtonElement = !useWrapper && !!component.use_button_element
  const wrapperEl = useWrapper ? 'a' : useButtonElement ? 'button' : 'div'
  const Inner = getInner({
    component,
    page,
    editingMode,
    attrs,
    userData,
    builderVersion,
  })
  const stateOnValid = !!component.needs_validation_passed
    ? component.validation_show_state ||
    page.show_next_button_on_incomplete ||
    flow.show_next_button_on_incomplete ||
    ''
    : page.change_validate_default_behaviour || flow.change_validate_default_behaviour || ''

  if (!flow.form_wrapper) attrs.add('form', `flow-${flow.id}`)
  attrs
    .addController(CONTROLLER)
    .addAction(
      `click->${CONTROLLER}#performButtonAction`,
      `${CONTROLLER}:validate->flow#triggerFormValidation`,
      `${CONTROLLER}:next->flow#next`,
      `${CONTROLLER}:prev->flow#prev`,
      `${CONTROLLER}:to-page->flow#goToPage`,
      `${CONTROLLER}:reset->flow#reset`,
      `${CONTROLLER}:info-box->flow#openInfoBox`,
      `${CONTROLLER}:close-popup->flow#closeInfoBox`,
      `${CONTROLLER}:actions->flow#dispatchAction`,
      `${CONTROLLER}:update->flow#update`,
      `keydown->${CONTROLLER}#evaluateRunningActions`
    )
    .addValueIfExists(CONTROLLER, 'action', component.action)
    .addValueIfExists(CONTROLLER, 'skip-validation', `${!!component.allow_skip_validation}`)
    .addValueIfExists(CONTROLLER, 'require-valid-page', `${!!component.needs_validation_passed}`)
    .addValueIfExists(CONTROLLER, 'state-on-valid', stateOnValid)

  if (useButtonElement) attrs.add('type', 'button')
  else attrs.add('tabindex', '0')

  if (component.hide_if_no_nav_target) {
    attrs.addValue(CONTROLLER, 'require-nav-target', `${component.hide_if_no_nav_target}`)
    attrs.add('style', 'display: none;')
    attrs.add('data-condition-override-value', 'true')
  }

  if (component.action === 'open-info-box')
    attrs.addValueIfExists(CONTROLLER, 'info-box', component.info_box_key)

  if (component.shouldDownloadUrl) {
    const downloadName = component.downloadFilename || 'download'
    attrs.add('download', downloadName)
    //* First i need to get the extension of the file, that could be .svg .pdf .jpg .jpeg etc...
    component.actionUrl = component.actionUrl || ''
    const arrayOfUrl = component.actionUrl.split('.') || []
    const extension = arrayOfUrl[arrayOfUrl.length - 1]

    //* Define the common query parameters
    let queryParams = component.actionUrl && component.actionUrl.includes('?') ? '&' : '?'
    //* If the user has specified a downloadFilename and the extension is in between 3 or 4 characters long, then add the download query param
    if (component.downloadFilename && extension.length >= 3 && extension.length <= 4) {
      queryParams += `download=${downloadName}.${extension}`
    } else {
      queryParams += 'download=true'
    }
    //* Append the query parameters to the actionUrl
    component.actionUrl += queryParams

    //* Add template controller if the downloadFilename has {{
    //* Commented out since at this point I can't add 2 template target and the component already have href
    // if (component.downloadFilename && component.downloadFilename.includes('{{'))
    //   attrs
    //     .addController('template')
    //     .addValue('template', 'target', 'download')
    //     .addValueIfExists('template', 'always-rerender', component.always_rerender)
  }

  if (useWrapper) {
    attrs
      .addDOMClass(['CustomButton_link', 'animate'])
      .add('rel', ['noopener', 'noreferrer'])
      .add('target', component.openUrlInNewTab ? '_blank' : '_self')
    if (component.actionUrl) {
      const actionUrl = replaceStorageURLsFromFirebaseToCloudflare(component.actionUrl) || ''
      const url =
        replaceStorageURLsFromFirebaseToCloudflare(renderTemplate(actionUrl, userData)) || ''
      attrs.add('href', url)

      //* Add template controller if the actionUrl has {{
      if (component.actionUrl && component.actionUrl.includes('{{'))
        attrs
          .addController('template')
          .addValue('template', 'target', 'href')
          .addValueIfExists('template', 'text', encodeURIComponent(actionUrl || ''))
          .addValueIfExists('template', 'always-rerender', component.always_rerender)

      if (component.ariaLabel) attrs.add('aria-label', component.ariaLabel)
    }
  }

  if (component.tooltip) {
    attrs.addController('tooltip').addValue('tooltip', 'text', component.tooltip)
    if (component.tooltip_description)
      attrs.addValue('tooltip', 'description', component.tooltip_description)
    if (component.tooltip_position)
      attrs.addValue('tooltip', 'position', component.tooltip_position)
  }

  return `<${wrapperEl} ${attrs}>${getIcon({
    component,
    page,
    attrs,
    builderVersion,
  })}${Inner}</${wrapperEl}>`
}

function getInner({
  component,
  page,
  editingMode,
  attrs,
  userData,
  builderVersion,
}: {
  component: CustomButton
  page: FlowPage
  editingMode: boolean
  attrs: Attrs
  userData?: { [key: string]: unknown }
  builderVersion: BaseBuilderVersion
}): string {
  const textWithURLReplacement = replaceStorageURLsFromFirebaseToCloudflare(component.text)
  const textAfterTemplate =
    replaceStorageURLsFromFirebaseToCloudflare(renderTemplate(textWithURLReplacement, userData)) ||
    ''

  const textAttrs = new Attrs(attrs.static_render).addTarget(CONTROLLER, 'text').addDOMClass(
    'text',
    generateElementSelectors({
      elementType: 'ButtonText',
      component,
      page,
      elementTags: ['ButtonText'],
      builderVersion,
    })
  )

  //* Add language controller for the text
  addLanguageController({ attrs: textAttrs, component, keys: ['text'] })

  //* I add attributes to specify if text is custom HTML
  if (component.richText) textAttrs.add('data-custom-html-text', 'true')

  //* Add template controller if the text has {{
  if (component.text && component.text.includes('{{'))
    textAttrs
      .addController('template')
      .addValueIfExists('template', 'text', encodeURIComponent(textWithURLReplacement || ''))
      .addValueIfExists('template', 'always-rerender', component.always_rerender)

  if (component.page_reactive_classes) textAttrs.addReactiveElement()

  const descriptionWithURLReplacement =
    replaceStorageURLsFromFirebaseToCloudflare(component.description) || ''
  const descriptionAfterTemplate =
    replaceStorageURLsFromFirebaseToCloudflare(
      renderTemplate(descriptionWithURLReplacement, userData)
    ) || ''

  const descAttrs = new Attrs(attrs.static_render).addTarget(CONTROLLER, 'description').addDOMClass(
    'description',
    generateElementSelectors({
      elementType: 'ButtonDescription',
      component,
      page,
      elementTags: ['ButtonDescription'],
      builderVersion,
    })
  )

  //* Add language controller for the description
  addLanguageController({ attrs: descAttrs, component, keys: ['description'] })

  //* I add attributes to specify if description is custom HTML
  if (component.richDescription) descAttrs.add('data-custom-html-description', 'true')

  //* Add template controller if the description has {{
  if (component.description && component.description.includes('{{'))
    descAttrs
      .addController('template')
      .addValueIfExists('template', 'text', encodeURIComponent(descriptionWithURLReplacement || ''))
      .addValueIfExists('template', 'always-rerender', component.always_rerender)

  if (component.page_reactive_classes) descAttrs.addReactiveElement()

  const text = textWithURLReplacement ? `<div ${textAttrs}>${textAfterTemplate}</div>` : ''

  const description = descriptionWithURLReplacement
    ? `<div ${descAttrs}>${descriptionAfterTemplate}</div>`
    : ''

  const textWrapperAttrs = new Attrs(attrs.static_render)
    .addTarget(CONTROLLER, 'text-wrapper')
    .addDOMClass(
      'text-wrapper',
      generateElementSelectors({
        elementType: 'ButtonTextWrapper',
        component,
        page,
        elementTags: ['ButtonTextWrapper'],
        builderVersion,
      })
    )
  return `<div ${textWrapperAttrs}>${text}${description}</div>`
}

function getIcon({
  component,
  page,
  attrs,
  builderVersion,
}: {
  component: CustomButton
  page: FlowPage
  attrs: Attrs
  builderVersion: BaseBuilderVersion
}): string {
  const iconAttrs = new Attrs(attrs.static_render).addDOMClass(['button-icon'])
  if (component.page_reactive_classes) iconAttrs.addReactiveElement()
  if (component.icon) {
    let [library, name] = iconToString(component.icon)
    if (!name) {
      name = library
      library = 'fa'
    }

    const iconClasses = generateElementSelectors({
      elementType: 'ButtonIcon',
      component,
      page,
      elementTags: ['ButtonIcon', 'GeneralButtonIcon', 'Icon'],
      builderVersion,
    })
    iconAttrs.addIcon(library, name).addDOMClass(iconClasses)

    return `<svg ${iconAttrs}></svg>`
  } else if (component.emojiIcon) {
    iconAttrs.addDOMClass([
      'icon',
      ...generateElementSelectors({
        elementType: 'ButtonIcon',
        component,
        page,
        elementTags: ['ButtonIcon', 'GeneralButtonIcon', 'Icon'],
        builderVersion,
      }),
    ])
    return `<span ${iconAttrs}>${component.emojiIcon}</span>`
  } else if (component.imageUrl) {
    const src = replaceStorageURLsFromFirebaseToCloudflare(component.imageUrl) || ''
    iconAttrs.addDOMClass([
      'img',
      ...generateElementSelectors({
        elementType: 'ButtonIconImage',
        component,
        page,
        elementTags: ['ButtonIcon', 'GeneralButtonIcon', 'ButtonIconImage', 'Icon'],
        builderVersion,
      }),
    ])
    const imgAttrs = new Attrs(attrs.static_render).add('src', src)
    //* Add language controller for the imageUrl and imageAltText
    addLanguageController({
      attrs: imgAttrs,
      component,
      keys: ['imageUrl', 'imageAltText'],
      mapKeys: { imageUrl: 'src', imageAltText: 'alt_text' },
    })

    //* I add template controller if the imageUrl has {{
    if (component.imageUrl && component.imageUrl.includes('{{'))
      imgAttrs
        .addController('template')
        .addValue('template', 'target', 'src')
        .addValueIfExists('template', 'text', encodeURIComponent(component.imageUrl || ''))
        .addValueIfExists('template', 'always-rerender', component.always_rerender)

    if (component.imageAltText) imgAttrs.add('alt', component.imageAltText)
    return `<span ${iconAttrs}><img ${imgAttrs}></span>`
  }
  return ''
}
